import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { authFetch, setAuthToken, clearAuthToken } from "./lib/auth";
import "@/App.css";
// Pages
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import NumberTraining from "./pages/NumberTraining";
import WordTraining from "./pages/WordTraining";
import NamesTraining from "./pages/NamesTraining";
import WorkingMemoryTraining from "./pages/WorkingMemoryTraining";
import History from "./pages/History";
import Leaderboard from "./pages/Leaderboard";
import Admin from "./pages/Admin";
import Profile from "./pages/Profile";
import Paywall from "./pages/Paywall";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import SubscriptionCancel from "./pages/SubscriptionCancel";
import Science from "./pages/Science";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await authFetch(`${API}/auth/me`);
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
        clearAuthToken();
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authFetch(`${API}/auth/logout`, { method: "POST" });
    } catch (error) {
      console.error("Logout error:", error);
    }
    clearAuthToken();
    setUser(null);
  };

  return { user, setUser, loading, checkAuth, logout };
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await fetch(`${API}/auth/session`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ session_id: sessionId }),
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.session_token) {
              setAuthToken(data.session_token);
            }
            navigate("/dashboard", { state: { user: data }, replace: true });
          } else {
            navigate("/", { replace: true });
          }
        } catch (error) {
          console.error("Auth error:", error);
          navigate("/", { replace: true });
        }
      } else {
        navigate("/", { replace: true });
      }
    };

    processAuth();
  }, [location, navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    if (location.state?.user) return;

    const checkAuth = async () => {
      try {
        const response = await authFetch(`${API}/auth/me`);
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
          clearAuthToken();
          navigate("/", { replace: true });
        }
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/", { replace: true });
      }
    };

    checkAuth();
  }, [location.state, navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children({ user, setUser });
};

// Subscription Gate — wraps training routes only
const RequireSubscription = ({ children }) => {
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking, ok, locked

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${API}/subscription/me`);
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          if (data.is_active) {
            setState("ok");
          } else {
            setState("locked");
            navigate("/paywall", { replace: true });
          }
        } else {
          setState("locked");
        }
      } catch (e) {
        if (!cancelled) setState("locked");
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  if (state === "checking") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  if (state === "locked") return null;
  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check for session_id in hash synchronously
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {({ user }) => <Dashboard user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/train/numbers"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <RequireSubscription>
                <NumberTraining user={user} />
              </RequireSubscription>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/train/words"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <RequireSubscription>
                <WordTraining user={user} />
              </RequireSubscription>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/train/names"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <RequireSubscription>
                <NamesTraining user={user} />
              </RequireSubscription>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/train/working-memory"
        element={
          <ProtectedRoute>
            {({ user }) => (
              <RequireSubscription>
                <WorkingMemoryTraining user={user} />
              </RequireSubscription>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedRoute>
            {({ user }) => <History user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/leaderboard"
        element={
          <ProtectedRoute>
            {({ user }) => <Leaderboard user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            {({ user }) => <Admin user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            {({ user }) => <Profile user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/paywall"
        element={
          <ProtectedRoute>
            {() => <Paywall />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription/success"
        element={
          <ProtectedRoute>
            {() => <SubscriptionSuccess />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription/cancel"
        element={
          <ProtectedRoute>
            {() => <SubscriptionCancel />}
          </ProtectedRoute>
        }
      />
      <Route path="/science" element={<Science />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-background">
      <div className="noise-overlay"></div>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
